import React, { useState, useEffect, useRef } from 'react';
import { Box, Text, HStack, Button } from '@chakra-ui/react';
import { BUSINESS_CATEGORIES } from '../constants';

import InfluencerEventsList from '../components/InfluencerEventsList';
import UpcomingEventsList from '../components/UpcomingEventsList';
import MyEventsList from '../components/MyEventsList';
import InfluencerMyProfile from '../components/InfluencerMyProfile';
import SupportButton from '../components/SupportButton';


const InfluencerHomePage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('restaurant');
  const [selectedTab, setSelectedTab] = useState('home');
  const [selectedFavoritesTab, setSelectedFavoritesTab] = useState('upcoming');
  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);
  const categoryScrollRef = useRef<HTMLDivElement>(null);
  const categoryButtonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const scrollLeftRef = useRef(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleLogoutDialogStateChange = (isOpen: boolean) => {
    setIsLogoutDialogOpen(isOpen);
  };

  const categories = BUSINESS_CATEGORIES;

  const getCurrentDate = () => {
    const date = new Date();
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleCategoryClick = (categoryId: string, e?: React.MouseEvent) => {
    // Only handle click if we're not dragging
    if (isDraggingRef.current) {
      return;
    }

    setSelectedCategory(categoryId);

    // Scroll to the selected category button
    const buttonElement = categoryButtonRefs.current[categoryId];
    if (buttonElement && categoryScrollRef.current) {
      const container = categoryScrollRef.current;
      const buttonRect = buttonElement.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      const scrollLeft = container.scrollLeft;
      const buttonLeft = buttonRect.left - containerRect.left + scrollLeft;
      const buttonWidth = buttonRect.width;
      const containerWidth = containerRect.width;

      // Calculate scroll position to center the button in the container
      const targetScroll = buttonLeft - (containerWidth / 2) + (buttonWidth / 2);

      container.scrollTo({
        left: targetScroll,
        behavior: 'smooth'
      });
    }
  };

  // Mouse drag handlers
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!categoryScrollRef.current) return;

    isDraggingRef.current = true;
    const rect = categoryScrollRef.current.getBoundingClientRect();
    startXRef.current = e.pageX - rect.left;
    scrollLeftRef.current = categoryScrollRef.current.scrollLeft;
    categoryScrollRef.current.style.cursor = 'grabbing';
    categoryScrollRef.current.style.userSelect = 'none';
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDraggingRef.current || !categoryScrollRef.current) return;
    e.preventDefault();

    const rect = categoryScrollRef.current.getBoundingClientRect();
    const pointerX = e.pageX - rect.left;
    const walk = (pointerX - startXRef.current) * 2; // Scroll speed multiplier
    categoryScrollRef.current.scrollLeft = scrollLeftRef.current - walk;
  };

  const handleMouseUp = () => {
    isDraggingRef.current = false;
    if (categoryScrollRef.current) {
      categoryScrollRef.current.style.cursor = 'grab';
      categoryScrollRef.current.style.userSelect = 'auto';
    }
  };

  const handleMouseLeave = () => {
    isDraggingRef.current = false;
    if (categoryScrollRef.current) {
      categoryScrollRef.current.style.cursor = 'grab';
      categoryScrollRef.current.style.userSelect = 'auto';
    }
  };

  // Touch handlers
  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!categoryScrollRef.current) return;

    isDraggingRef.current = true;
    const rect = categoryScrollRef.current.getBoundingClientRect();
    startXRef.current = e.touches[0].pageX - rect.left;
    scrollLeftRef.current = categoryScrollRef.current.scrollLeft;
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isDraggingRef.current || !categoryScrollRef.current) return;

    e.preventDefault(); // Prevent default scrolling
    const rect = categoryScrollRef.current.getBoundingClientRect();
    const pointerX = e.touches[0].pageX - rect.left;
    const walk = (pointerX - startXRef.current) * 2; // Scroll speed multiplier
    categoryScrollRef.current.scrollLeft = scrollLeftRef.current - walk;
  };

  const handleTouchEnd = () => {
    isDraggingRef.current = false;
  };

  // Global mouse events for dragging outside the element
  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (!isDraggingRef.current || !categoryScrollRef.current) return;

      const rect = categoryScrollRef.current.getBoundingClientRect();
      const pointerX = e.pageX - rect.left;
      const walk = (pointerX - startXRef.current) * 2;
      categoryScrollRef.current.scrollLeft = scrollLeftRef.current - walk;
    };

    const handleGlobalMouseUp = () => {
      if (isDraggingRef.current) {
        isDraggingRef.current = false;
        if (categoryScrollRef.current) {
          categoryScrollRef.current.style.cursor = 'grab';
          categoryScrollRef.current.style.userSelect = 'auto';
        }
      }
    };

    document.addEventListener('mousemove', handleGlobalMouseMove);
    document.addEventListener('mouseup', handleGlobalMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, []);

  return (
    <Box 
      className="base-container-long" 
      ref={scrollContainerRef}
      data-scroll-container="true"      
    >
      {/* Navigation Bar */}
      <Box className="navigation-bar" justifyContent="space-between" paddingRight="24px">
        <Text className="title-text"  >
          {selectedTab === 'favorites' ? 'Events' : 'Home'}
        </Text>
        <SupportButton
          borderColor="#C6D959"
          hoverBorderColor="#C6D959"
          hoverBackgroundColor="rgba(198, 217, 89, 0.1)"
          backgroundColor="rgba(28, 28, 30, 0.8)"
        />
      </Box>

      {/* Date Display */}
      <Box
        position="absolute"
        top="104px"
        left="24px"
        zIndex={2}
      >
        <Text className="subtitle-text" >
          {getCurrentDate()}
        </Text>
      </Box>

      {/* Divider */}
      <Box className="navigation-bar-divider" />

      {/* Content based on selected tab */}
      {selectedTab === 'home' && (
        <>
          {/* Category Filter Chips Carousel */}
          <Box
            className="button-category-box"
            ref={categoryScrollRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <HStack
              spacing="8px"
              width="max-content"
              paddingLeft="24px"
              paddingRight="24px"
              paddingBottom="20px"
            >
              {categories.map((category) => (
                <Button
                  key={category.id}
                  className="button-category"
                  ref={(el: HTMLButtonElement | null) => {
                    categoryButtonRefs.current[category.id] = el;
                  }}
                  backgroundColor={selectedCategory === category.id ? "#C6D959" : "#1D1D1D"}
                  color={selectedCategory === category.id ? "#120F1B" : "#90909A"}
                  fontWeight={selectedCategory === category.id ? "500" : "400"}
                  _hover={{
                    backgroundColor: selectedCategory === category.id ? "#b8cc4a" : "#2A2A2A"
                  }}
                  onClick={() => handleCategoryClick(category.id)}
                >
                  {category.label}
                </Button>
              ))}
            </HStack>
          </Box>

          {/* Events List Container */}
          <Box
            //className='form-container'
            position="absolute"
            top="221px"
            left="24px"
            right="24px"
            bottom="98px"
            overflowY="auto"
            overflowX="hidden"
            zIndex={2}
            sx={{
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }}
          >
            {/* Events List for Influencers */}
            <InfluencerEventsList selectedCategory={selectedCategory} />
          </Box>
        </>
      )}

      {selectedTab === 'favorites' && (
        /* Favorites List */
        <Box
          position="absolute"
          top="161px"
          left="24px"
          right="24px"
          bottom="98px"
          overflowY="auto"
          overflowX="hidden"
          zIndex={2}
          sx={{
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
        >
          {/* Favorites Tab Chips */}
          <Box
            marginBottom="20px"
            position="sticky"
            top="0"
            zIndex={3}
            backgroundColor="#020105"
            paddingTop="0"
            paddingBottom="10px"
          >
            <HStack spacing="8px">
              <Button
                className="events-upcoming-favorites-button"
                backgroundColor={selectedFavoritesTab === 'upcoming' ? "#C6D959" : "#1D1D1D"}
                color={selectedFavoritesTab === 'upcoming' ? "#120F1B" : "#90909A"}
                fontWeight={selectedFavoritesTab === 'upcoming' ? "500" : "400"}
                onClick={() => setSelectedFavoritesTab('upcoming')}                
                _hover={{
                  backgroundColor: selectedFavoritesTab === 'upcoming' ? "#b8cc4a" : "#2A2A2A"
                }}
              >
                Upcoming events
              </Button>
              <Button
                className="events-upcoming-favorites-button"                
                backgroundColor={selectedFavoritesTab === 'my-events' ? "#C6D959" : "#1D1D1D"}
                color={selectedFavoritesTab === 'my-events' ? "#120F1B" : "#90909A"}
                fontWeight={selectedFavoritesTab === 'my-events' ? "500" : "400"}                
                _hover={{
                  backgroundColor: selectedFavoritesTab === 'my-events' ? "#b8cc4a" : "#2A2A2A"
                }}
                onClick={() => setSelectedFavoritesTab('my-events')}
              >
                My events
              </Button>
            </HStack>
          </Box>

          {/* Render appropriate list based on selected tab */}
          {selectedFavoritesTab === 'upcoming' ? (
            <UpcomingEventsList />
          ) : (
            <MyEventsList />
          )}
        </Box>
      )}

      {selectedTab === 'profile' && (
        <InfluencerMyProfile onDialogStateChange={handleLogoutDialogStateChange} />
      )}

      {/* Background Overlay when logout dialog is open */}
      {isLogoutDialogOpen && (
        <Box
          position="absolute"
          top="0"
          left="0"
          width="100%"
          height="100%"
          backgroundColor="rgba(28, 28, 30, 0.9)"
          zIndex={1}
        />
      )}

      {/* Bottom Navigation Buttons */}
      {!isLogoutDialogOpen && (
        <Box className="bottom-navigation-bar-box" >
        {/* Home Button */}
        <Button
          className="bottom-navigation-bar-button"
          onClick={() => setSelectedTab('home')}
        >
          <svg width="18" height="20" viewBox="0 0 18 20" fill="none">
            <path
              d="M9 0L0 7V20H7V12H11V20H18V7L9 0Z"
              fill={selectedTab === 'home' ? "#C6D959" : "none"}
              stroke={selectedTab === 'home' ? "none" : "#90909A"}
              strokeWidth="1.5"
            />
          </svg>
        </Button>

        {/* Favorites Button */}
        <Button
          className="bottom-navigation-bar-button"
          onClick={() => setSelectedTab('favorites')}
        >
          <svg width="28" height="26" viewBox="0 0 16 14" fill="none">
            <path
              d="M8 13.5C8 13.5 1.5 8 1.5 4.5C1.5 2.5 3 1 5 1C6.5 1 8 2.5 8 2.5C8 2.5 9.5 1 11 1C13 1 14.5 2.5 14.5 4.5C14.5 8 8 13.5 8 13.5Z"
              fill={selectedTab === 'favorites' ? "#C6D959" : "none"}
              stroke={selectedTab === 'favorites' ? "none" : "#90909A"}
              strokeWidth="1"
            />
          </svg>

        </Button>

        {/* Profile Button */}
        <Button
          className="bottom-navigation-bar-button"
          onClick={() => setSelectedTab('profile')}
        >
          <svg width="16" height="18" viewBox="0 0 16 18" fill="none">
            <path
              d="M8 0C5.79 0 4 1.79 4 4C4 6.21 5.79 8 8 8C10.21 8 12 6.21 12 4C12 1.79 10.21 0 8 0ZM8 10C5.33 10 0 11.34 0 14V16C0 17.1 0.9 18 2 18H14C15.1 18 16 17.1 16 16V14C16 11.34 10.67 10 8 10Z"
              fill={selectedTab === 'profile' ? "#C6D959" : "none"}
              stroke={selectedTab === 'profile' ? "none" : "#90909A"}
              strokeWidth="1.5"
            />
          </svg>

        </Button>
        </Box>
      )}
    </Box>
  );
};

export default InfluencerHomePage;
